import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Navbar } from '@/components/Navbar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Loader2, CreditCard, CheckCircle2, ExternalLink, 
  Calendar, ArrowRight, AlertCircle 
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BillingPage() {
  const { user, token, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    // Check for successful checkout
    const sessionId = searchParams.get('session_id');
    const success = searchParams.get('success');
    
    if (sessionId && success) {
      pollCheckoutStatus(sessionId);
    } else {
      fetchSubscription();
    }
  }, []);

  const pollCheckoutStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 5) {
      toast.error('Could not verify payment. Please refresh the page.');
      fetchSubscription();
      return;
    }

    try {
      const response = await axios.get(`${API}/billing/checkout/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.payment_status === 'paid') {
        toast.success('Subscription activated! Welcome to Pro!');
        await refreshUser();
        fetchSubscription();
        // Clear URL params
        navigate('/app/billing', { replace: true });
      } else {
        setTimeout(() => pollCheckoutStatus(sessionId, attempts + 1), 2000);
      }
    } catch (error) {
      console.error('Checkout status error:', error);
      setTimeout(() => pollCheckoutStatus(sessionId, attempts + 1), 2000);
    }
  };

  const fetchSubscription = async () => {
    try {
      const response = await axios.get(`${API}/billing/subscription`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscription(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const openBillingPortal = async () => {
    setPortalLoading(true);
    try {
      const response = await axios.post(
        `${API}/billing/portal`,
        { origin_url: window.location.origin },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = response.data.url;
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to open billing portal';
      toast.error(message);
    } finally {
      setPortalLoading(false);
    }
  };

  const getPlanPrice = (plan) => {
    const prices = { free: 0, pro: 19, business: 49 };
    return prices[plan] || 0;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-slate-600" />
        </div>
      </div>
    );
  }

  const isFreePlan = !subscription?.stripe_subscription_id || user?.plan === 'free';
  const isCanceling = subscription?.cancel_at_period_end;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="billing-page">
      <Navbar />

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="font-heading font-bold text-2xl text-slate-900 mb-8">Billing & Subscription</h1>

        {/* Current Plan */}
        <Card className="bg-white border border-slate-200 mb-6">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Current Plan
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="flex items-center gap-3">
                  <span className="font-heading font-bold text-2xl text-slate-900 capitalize">
                    {user?.plan || 'Free'}
                  </span>
                  {subscription?.status === 'active' && !isCanceling && (
                    <Badge className="bg-lime-100 text-lime-700">Active</Badge>
                  )}
                  {isCanceling && (
                    <Badge className="bg-orange-100 text-orange-700">Canceling</Badge>
                  )}
                </div>
                <p className="text-slate-500 mt-1">
                  ${getPlanPrice(user?.plan)}/month
                </p>
              </div>

              {!isFreePlan && (
                <Button 
                  onClick={openBillingPortal}
                  disabled={portalLoading}
                  variant="outline"
                >
                  {portalLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      Manage Subscription
                      <ExternalLink className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              )}
            </div>

            {isCanceling && subscription?.current_period_end && (
              <div className="flex items-start gap-3 p-4 bg-orange-50 rounded-lg">
                <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-orange-800">Subscription Canceling</p>
                  <p className="text-sm text-orange-700">
                    Your subscription will end on{' '}
                    {new Date(subscription.current_period_end).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}. You'll be downgraded to the Free plan after that.
                  </p>
                </div>
              </div>
            )}

            {!isFreePlan && subscription?.current_period_end && !isCanceling && (
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Calendar className="w-4 h-4" />
                Next billing date:{' '}
                {new Date(subscription.current_period_end).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upgrade CTA (for free users) */}
        {isFreePlan && (
          <Card className="bg-gradient-to-r from-slate-900 to-slate-800 text-white">
            <CardContent className="p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-lg">Upgrade to Pro</h3>
                  <p className="text-slate-300">Unlock 50 analyses/month and unlimited exports</p>
                </div>
              </div>

              <ul className="space-y-2 mb-6">
                <li className="flex items-center gap-2 text-sm text-slate-200">
                  <CheckCircle2 className="w-4 h-4 text-lime-400" />
                  50 resume analyses per month
                </li>
                <li className="flex items-center gap-2 text-sm text-slate-200">
                  <CheckCircle2 className="w-4 h-4 text-lime-400" />
                  Unlimited PDF exports
                </li>
                <li className="flex items-center gap-2 text-sm text-slate-200">
                  <CheckCircle2 className="w-4 h-4 text-lime-400" />
                  Priority AI processing
                </li>
              </ul>

              <Button 
                onClick={() => navigate('/pricing')}
                className="bg-white text-slate-900 hover:bg-slate-100"
              >
                View Pricing
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
